import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AiOutlineSearch } from 'react-icons/ai';
import { BsBullseye, BsPlusLg } from 'react-icons/bs';
import { IoCloseCircle } from 'react-icons/io5';
import { Menu } from '../../components/Menu/Menu';
import LoadingWindow from '../../components/LoadingWindow';
import { automaticCloseAlert } from '../../functions/alerts';
import { getValueCookie } from '../../services/cookieService';
import {
    GOAL_STATUS,
    GOAL_TYPE,
    formatDate,
    formatDateRange,
    formatMoney,
    getCurrentCompanyId,
    groupGoalsByTarget,
    pickCurrentGoal,
    salespersonLabel,
    sortGoalsNewestFirst,
    summarizeCustomerSales,
} from '../../services/goalsService';
import GoalFormDialog from './GoalFormDialog';
import {
    GoalMeter,
    GoalStatusBadge,
    GoalsDemoNotice,
    GoalsDenied,
    GoalsTable,
    goalDetailPath,
    goalStatusLabel,
    useGoalDelete,
    useGoalsData,
    useGoalsWithProgress,
    useTargetNames,
} from './GoalParts';
import styles from '../../css/Goals.module.css';

/**
 * Goals — dashboard general de metas por vendedor y por customer (TypeUser '1').
 * Demo para autorización del cliente (2026-11-04): las metas viven en goalsService.js (mock);
 * buscar "TODO: BACKEND" para conectarlas.
 */

const MAX_SEARCH_RESULTS = 8;
const MAX_LATEST_GOALS = 5;
const NO_GOAL_PAGE_SIZE = 25;

const STATUS_FILTERS = [GOAL_STATUS.inProgress, GOAL_STATUS.upcoming, GOAL_STATUS.achieved, GOAL_STATUS.missed];

// Una fila por vendedor o customer, con su meta de hoy. Sin meta, muestra cómo le fue en la última.
function TargetStatusTable({ rows, goalType, caption, onSetGoal }) {
    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <caption className={styles.srOnly}>{caption}</caption>
                <thead>
                    <tr>
                        <th scope="col">{goalType === GOAL_TYPE.customer ? 'Customer' : 'Salesperson'}</th>
                        <th scope="col">Current goal</th>
                        <th scope="col" className={styles.numeric}>Expected</th>
                        <th scope="col" className={styles.numeric}>Sold</th>
                        <th scope="col">Progress</th>
                        <th scope="col">Status</th>
                        <th scope="col"><span className={styles.srOnly}>Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(({ no, name, note, current, last }) => (
                        <tr key={no}>
                            <td data-label={goalType === GOAL_TYPE.customer ? 'Customer' : 'Salesperson'} className={styles.cellPrimary}>
                                <Link className={styles.tableLink} to={goalDetailPath(goalType, no)}>{name}</Link>
                                {note && <span className={styles.cellNote}>{note}</span>}
                            </td>
                            {current ? (
                                <>
                                    <td data-label="Current goal">{formatDateRange(current.startDate, current.endDate)}</td>
                                    <td data-label="Expected" className={styles.numeric}>{formatMoney(current.targetAmount)}</td>
                                    <td data-label="Sold" className={styles.numeric}>
                                        {current.status === GOAL_STATUS.upcoming || current.achieved === null ? '—' : formatMoney(current.achieved)}
                                    </td>
                                    <td data-label="Progress" className={styles.cellMeter}><GoalMeter goal={current} /></td>
                                    <td data-label="Status"><GoalStatusBadge status={current.status} /></td>
                                </>
                            ) : (
                                <td colSpan={5} data-label="Current goal" className={styles.cellEmpty}>
                                    <span className={styles.cellStack}>
                                        No current goal
                                        {last && <span className={styles.cellNote}>Last goal ended {formatDate(last.endDate)}: {goalStatusLabel(last.status)}</span>}
                                    </span>
                                </td>
                            )}
                            <td className={styles.cellActions}>
                                <button type="button" className={styles.btnSecondary} onClick={() => onSetGoal(goalType, no)}>
                                    <BsPlusLg aria-hidden="true" /> Set goal
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CustomerSearch({ customers, currentGoalOf, onSetGoal }) {
    const [query, setQuery] = useState('');

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        return customers.filter((c) => c.searchText.includes(q)).slice(0, MAX_SEARCH_RESULTS);
    }, [query, customers]);

    return (
        <section className={styles.panel} aria-labelledby="goals-search-title">
            <h2 id="goals-search-title" className={styles.panelTitle}>Find a customer</h2>
            <div className={styles.searchBox}>
                <AiOutlineSearch className={styles.searchIcon} aria-hidden="true" />
                <input
                    className={styles.searchInput}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Name, number, city or phone…"
                    aria-label="Find a customer"
                    autoComplete="off"
                />
                {query && (
                    <button type="button" className={styles.searchClear} onClick={() => setQuery('')} aria-label="Clear search">
                        <IoCloseCircle aria-hidden="true" />
                    </button>
                )}
            </div>
            {query.trim() && results.length === 0 && <p className={styles.emptyInline}>No customers match this search.</p>}
            {results.length > 0 && (
                <ul className={styles.searchResults}>
                    {results.map((c) => {
                        const current = currentGoalOf(c.customerNo);
                        return (
                            <li key={c.customerNo} className={styles.searchResult}>
                                <div className={styles.searchResultText}>
                                    <Link className={styles.tableLink} to={goalDetailPath(GOAL_TYPE.customer, c.customerNo)}>{c.name}</Link>
                                    <span className={styles.cellNote}>{c.customerNo}{c.city ? ` · ${c.city}, ${c.state}` : ''}</span>
                                    {current
                                        ? <GoalStatusBadge status={current.status} />
                                        : <span className={styles.cellNote}>No current goal</span>}
                                </div>
                                <button type="button" className={styles.btnPrimary} onClick={() => onSetGoal(GOAL_TYPE.customer, c.customerNo)}>
                                    <BsPlusLg aria-hidden="true" /> Set goal
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

function CustomersWithoutGoal({ rows, onSetGoal }) {
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? rows.filter((r) => r.searchText.includes(q)) : rows;
    }, [query, rows]);

    const pageCount = Math.max(Math.ceil(filtered.length / NO_GOAL_PAGE_SIZE), 1);
    const safePage = Math.min(page, pageCount - 1);
    const visible = filtered.slice(safePage * NO_GOAL_PAGE_SIZE, (safePage + 1) * NO_GOAL_PAGE_SIZE);

    return (
        <section className={styles.section} aria-labelledby="goals-no-goal-title">
            <h2 id="goals-no-goal-title" className={styles.sectionTitle}>
                Customers without a current goal <span className={styles.sectionCount}>{rows.length.toLocaleString()}</span>
            </h2>
            <p className={styles.sectionNote}>Most recent sales first.</p>
            <div className={`${styles.searchBox} ${styles.sectionSearch}`}>
                <AiOutlineSearch className={styles.searchIcon} aria-hidden="true" />
                <input
                    className={styles.searchInput}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                    placeholder="Filter this list…"
                    aria-label="Filter customers without a current goal"
                    autoComplete="off"
                />
            </div>
            {visible.length === 0 ? (
                <p className={styles.empty}>{rows.length === 0 ? 'Every customer has a current goal.' : 'No customers match this filter.'}</p>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <caption className={styles.srOnly}>Customers without a current goal</caption>
                        <thead>
                            <tr>
                                <th scope="col">Customer</th>
                                <th scope="col">Salesperson</th>
                                <th scope="col">Last sale</th>
                                <th scope="col" className={styles.numeric}>Sales, last 12 months</th>
                                <th scope="col"><span className={styles.srOnly}>Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((r) => (
                                <tr key={r.customerNo}>
                                    <td data-label="Customer" className={styles.cellPrimary}>
                                        <Link className={styles.tableLink} to={goalDetailPath(GOAL_TYPE.customer, r.customerNo)}>{r.name}</Link>
                                        <span className={styles.cellNote}>{r.customerNo}{r.city ? ` · ${r.city}, ${r.state}` : ''}</span>
                                    </td>
                                    <td data-label="Salesperson">{r.salespersonNo || '—'}</td>
                                    <td data-label="Last sale">{r.lastSale ? formatDate(r.lastSale) : 'None in 12 months'}</td>
                                    <td data-label="Sales, last 12 months" className={styles.numeric}>{r.lastSale ? formatMoney(r.sales) : '—'}</td>
                                    <td className={styles.cellActions}>
                                        <button type="button" className={styles.btnSecondary} onClick={() => onSetGoal(GOAL_TYPE.customer, r.customerNo)}>
                                            <BsPlusLg aria-hidden="true" /> Set goal
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {pageCount > 1 && (
                <nav className={styles.pagination} aria-label="Customers without a current goal, pages">
                    <button type="button" className={styles.btnSecondary} disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Previous</button>
                    <span className={styles.paginationText}>Page {safePage + 1} of {pageCount}</span>
                    <button type="button" className={styles.btnSecondary} disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>Next</button>
                </nav>
            )}
        </section>
    );
}

function GoalsScreen() {
    const idCompany = useMemo(getCurrentCompanyId, []);
    const loadingRef = useRef(null);
    const { data, loadError, reload } = useGoalsData(idCompany, loadingRef);

    const [tab, setTab] = useState(GOAL_TYPE.salesperson);
    const [statusFilter, setStatusFilter] = useState('all');
    // null = cerrado; { goalType?, targetNo? } = formulario abierto.
    const [formTarget, setFormTarget] = useState(null);

    const goals = useGoalsWithProgress(data);
    const targetName = useTargetNames(data);
    const onDelete = useGoalDelete(idCompany, data, targetName, reload);

    const openForm = useCallback((goalType, targetNo) => setFormTarget({ goalType, targetNo }), []);
    const closeForm = useCallback(() => setFormTarget(null), []);

    const onSaved = async () => {
        setFormTarget(null);
        await reload();
        automaticCloseAlert('correct', 'Goal created');
    };

    const salespersonGoals = useMemo(() => goals.filter((g) => g.goalType === GOAL_TYPE.salesperson), [goals]);
    const customerGoals = useMemo(() => goals.filter((g) => g.goalType === GOAL_TYPE.customer), [goals]);
    const customerGroups = useMemo(() => groupGoalsByTarget(customerGoals), [customerGoals]);

    const currentCustomerGoal = useCallback((customerNo) => (
        data ? pickCurrentGoal(customerGroups.get(customerNo) || [], data.today) : null
    ), [customerGroups, data]);

    const salespersonRows = useMemo(() => {
        if (!data) return [];
        const groups = groupGoalsByTarget(salespersonGoals);
        const known = new Map(data.salespeople.map((s) => [s.salespersonNo, salespersonLabel(s)]));
        // Un vendedor con metas que ya no sale en el catálogo también se muestra.
        groups.forEach((_, no) => { if (!known.has(no)) known.set(no, no); });
        return Array.from(known.entries()).map(([no, name]) => {
            const own = groups.get(no) || [];
            return { no, name, current: pickCurrentGoal(own, data.today), last: sortGoalsNewestFirst(own)[0] || null };
        });
    }, [data, salespersonGoals]);

    const customerSales = useMemo(() => (
        data ? summarizeCustomerSales(data.invoices || [], data.today) : new Map()
    ), [data]);

    const { customersWithGoal, customersWithoutGoal } = useMemo(() => {
        const withGoal = [];
        const withoutGoal = [];
        if (data) {
            data.customers.forEach((c) => {
                const current = currentCustomerGoal(c.customerNo);
                if (current) {
                    withGoal.push({ no: c.customerNo, name: c.name, note: c.customerNo, current });
                } else {
                    withoutGoal.push({ ...c, ...(customerSales.get(c.customerNo) || { sales: 0, lastSale: '' }) });
                }
            });
            withGoal.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
            withoutGoal.sort((a, b) => b.lastSale.localeCompare(a.lastSale) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        }
        return { customersWithGoal: withGoal, customersWithoutGoal: withoutGoal };
    }, [data, currentCustomerGoal, customerSales]);

    const latestGoals = useMemo(() => (
        [...goals].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, MAX_LATEST_GOALS)
    ), [goals]);

    const filteredSalespersonGoals = useMemo(() => sortGoalsNewestFirst(
        salespersonGoals.filter((g) => statusFilter === 'all' || g.status === statusFilter),
    ), [salespersonGoals, statusFilter]);

    const countByStatus = (status) => goals.filter((g) => g.status === status).length;

    const renderKpi = (label, value) => (
        <div className={styles.kpi}>
            <span className={styles.kpiLabel}>{label}</span>
            <span className={styles.kpiValue}>{value}</span>
        </div>
    );

    const renderTab = (key, label) => (
        <button type="button" role="tab" aria-selected={tab === key} className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`} onClick={() => setTab(key)}>
            {label}
        </button>
    );

    const renderSalespeopleTab = () => (
        <>
            <section className={styles.section} aria-labelledby="goals-sp-status-title">
                <h2 id="goals-sp-status-title" className={styles.sectionTitle}>Status today</h2>
                {salespersonRows.length === 0
                    ? <p className={styles.empty}>No salespeople could be loaded.</p>
                    : <TargetStatusTable rows={salespersonRows} goalType={GOAL_TYPE.salesperson} caption="Salesperson goal status today" onSetGoal={openForm} />}
            </section>
            <section className={styles.section} aria-labelledby="goals-sp-all-title">
                <div className={styles.sectionHead}>
                    <h2 id="goals-sp-all-title" className={styles.sectionTitle}>
                        All salesperson goals <span className={styles.sectionCount}>{salespersonGoals.length}</span>
                    </h2>
                    <select className={`${styles.input} ${styles.sectionFilter}`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter goals by status">
                        <option value="all">All statuses</option>
                        {STATUS_FILTERS.map((s) => <option key={s} value={s}>{goalStatusLabel(s)}</option>)}
                    </select>
                </div>
                {filteredSalespersonGoals.length === 0
                    ? <p className={styles.empty}>No salesperson goals to show.</p>
                    : <GoalsTable goals={filteredSalespersonGoals} targetName={targetName} showTarget onDelete={onDelete} caption="All salesperson goals" />}
            </section>
        </>
    );

    const renderCustomersTab = () => (
        <>
            <section className={styles.section} aria-labelledby="goals-c-status-title">
                <h2 id="goals-c-status-title" className={styles.sectionTitle}>
                    Customers with a current goal <span className={styles.sectionCount}>{customersWithGoal.length}</span>
                </h2>
                {customersWithGoal.length === 0
                    ? <p className={styles.empty}>No customers have a current goal yet.</p>
                    : <TargetStatusTable rows={customersWithGoal} goalType={GOAL_TYPE.customer} caption="Customers with a current goal, status today" onSetGoal={openForm} />}
            </section>
            <CustomersWithoutGoal rows={customersWithoutGoal} onSetGoal={openForm} />
        </>
    );

    return (
        <div className={styles.page}>
            <Menu />
            <LoadingWindow ref={loadingRef} />

            <div className={styles.hero}>
                <div className={styles.heroLeft}>
                    <div className={styles.heroIconBox}><BsBullseye className={styles.heroIcon} aria-hidden="true" /></div>
                    <div className={styles.heroText}>
                        <h1 className={styles.heroTitle}>Goals</h1>
                        <p className={styles.heroSubtitle}>Sales goals for salespeople and customers, measured against invoice history</p>
                    </div>
                </div>
                {data && (
                    <button type="button" className={styles.heroBtn} onClick={() => setFormTarget({})}>
                        <BsPlusLg aria-hidden="true" /> New goal
                    </button>
                )}
            </div>

            <div className={styles.body}>
                {loadError && (
                    <div className={styles.empty} role="alert">
                        <p>{loadError}</p>
                        <button type="button" className={styles.btnPrimary} onClick={reload}>Try again</button>
                    </div>
                )}
                {data && !loadError && (
                    <>
                        <GoalsDemoNotice data={data} />
                        <div className={styles.kpis}>
                            {renderKpi('In progress', countByStatus(GOAL_STATUS.inProgress))}
                            {renderKpi('Achieved', countByStatus(GOAL_STATUS.achieved))}
                            {renderKpi('Missed', countByStatus(GOAL_STATUS.missed))}
                            {renderKpi('Customers without a current goal', customersWithoutGoal.length.toLocaleString())}
                        </div>

                        <CustomerSearch customers={data.customers} currentGoalOf={currentCustomerGoal} onSetGoal={openForm} />

                        <section className={styles.section} aria-labelledby="goals-latest-title">
                            <h2 id="goals-latest-title" className={styles.sectionTitle}>Latest goals created</h2>
                            {latestGoals.length === 0
                                ? <p className={styles.empty}>No goals have been created yet.</p>
                                : <GoalsTable goals={latestGoals} targetName={targetName} showTarget showCreated onDelete={onDelete} caption="The 5 most recently created goals" />}
                        </section>

                        <div className={styles.tabs} role="tablist" aria-label="Goal views">
                            {renderTab(GOAL_TYPE.salesperson, 'Salespeople')}
                            {renderTab(GOAL_TYPE.customer, 'Customers')}
                        </div>
                        {tab === GOAL_TYPE.salesperson ? renderSalespeopleTab() : renderCustomersTab()}
                    </>
                )}
            </div>

            {formTarget && (
                <GoalFormDialog
                    idCompany={idCompany}
                    data={data}
                    initial={formTarget}
                    onClose={closeForm}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}

export default function Goals() {
    if (getValueCookie('TypeUser') !== '1') return <GoalsDenied />;
    return <GoalsScreen />;
}
